/*
 Copyright (c) 2025 Metal Management Solutions Pty Ltd
 All rights reserved.
 This software is proprietary and confidential. Unauthorised copying,
 distribution, or modification of this software, via any medium, is
 strictly prohibited without prior written permission.
 Licensed under the terms of MMS-WIRE-LICENSE.
*/
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnInit,
    ViewEncapsulation
} from '@angular/core';
import {ComponentType} from '../../_models/component-type';
import {ConfigStub} from "../../_typing/config-stub";
import {ModelID} from '../../_typing/generic-types';
import {deepCopy} from "../../lib/utils";
import {ComponentCreatorConfig, CountResetFrequency} from "../../_typing/config/component-creator-config";
import {
    SelectManyComponentTypesComponent
} from "../../components/select-search-api/many/wrappers/select-many-component-types.component";
import {SelectSearchComponent} from "../../components/select-search/select-search.component";
import {COMMON_FORM_IMPORTS} from "../../shared/import-groups/Common";
import {
    ComponentNameFormulaSelectorComponent
} from "../component-name-formula-builder/component-name-formula-selector/component-name-formula-selector.component";
import {BeforeSaveAction} from "../../_typing/utils/before-save";
import {NotificationService} from "../../services/notification.service";
import {ComponentNameFormulaBuilderService} from "../../services/component-name-formula-builder.service";

@Component({
    selector: 'component-creator-form',
    templateUrl: './component-creator-form.component.html',
    styleUrls: ['./component-creator-form.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    imports: [
        SelectManyComponentTypesComponent,
        SelectSearchComponent,
        COMMON_FORM_IMPORTS,
        ComponentNameFormulaSelectorComponent
    ],
})
export class ComponentCreatorFormComponent implements OnInit, BeforeSaveAction {
    @Input() config: ComponentCreatorConfig;
    from_selection: ConfigStub<ComponentType>[];
    private defaultCountFormat: number = 4;
    private defaultCountReset: CountResetFrequency = 'yearly';

    constructor(private changeDetector: ChangeDetectorRef,
                private notification: NotificationService,
                private nameBuilder: ComponentNameFormulaBuilderService) {
    }

    ngOnInit(): void {
        if (!this.config.component_types) {
            this.config.component_types = [];
        }
        if (!this.config.days_in_advance) {
            this.config.days_in_advance = 2;
        }
        if (!this.config.days_in_arrears) {
            this.config.days_in_arrears = 2;
        }
        if (!this.config.component_type_config_map) {
            this.config.component_type_config_map = {};
        }
        this.from_selection = deepCopy(this.config.component_types);
        this.updateComponentTypeConfigMap();
    }

    simplifyConfig($event: ComponentType[]) {
        this.config.component_types = $event.map(ct => new ConfigStub<ComponentType>(ct));
        this.from_selection = deepCopy(this.config.component_types);
        if (!this.from_selection.find(ct => ct.id === this.config.default_type?.id)) {
            this.config.default_type = null;
        }
        this.updateComponentTypeConfigMap();
    }

    setSelectedFormula(componentType: Partial<ComponentType>, $event) {
        this._setDefaultComponentTypeConfigMap(componentType.id)
        this.config.component_type_config_map[componentType.id].formula = $event;
        if (!this.config.component_type_config_map[componentType.id].leading_zeros) {
            this.config.component_type_config_map[componentType.id].leading_zeros = this.config.component_type_config_map[componentType.id].formula ? this.defaultCountFormat : null;
        }
        if (!this.config.component_type_config_map[componentType.id].count_reset_frequency) {
            this.config.component_type_config_map[componentType.id].count_reset_frequency = this.config.component_type_config_map[componentType.id].formula ? this.defaultCountReset : null;
        }
    }

    isFormulaSelected(componentTypeId:ModelID): boolean {
        return this.config.component_type_config_map?.[componentTypeId]?.formula?.formula != null;
    }

    updateComponentTypeConfigMap(): void {
        if (this.config.component_types.length < 1) return;
        this.config.component_types.forEach(ct => {
            this._setDefaultComponentTypeConfigMap(ct.id)
        })
        const componentTypeIds = this.config.component_types.map(ct => ct.id);
        const keys = Object.keys(this.config.component_type_config_map)?.filter(key => !componentTypeIds.includes(key));

        keys.forEach(key => delete this.config.component_type_config_map[key]);
        this.changeDetector.markForCheck();
    }

    private _setDefaultComponentTypeConfigMap(componentTypeId) {
        if (!this.config.component_type_config_map[componentTypeId]) {
            this.config.component_type_config_map[componentTypeId] = {
                abbr: '',
                size: 1,
                formula: null,
                leading_zeros: null,
                count_reset_frequency: null,
                automatic_count: true
            };
        }
    }

    setCountFormat($event, componentTypeId: ModelID): void {
        this._setDefaultComponentTypeConfigMap(componentTypeId)
        this.config.component_type_config_map[componentTypeId].leading_zeros = $event;
    }

    setAutoCount($event, componentTypeId: ModelID): void {
        this._setDefaultComponentTypeConfigMap(componentTypeId)
        this.config.component_type_config_map[componentTypeId].automatic_count = $event;
    }

    setCountFrequency($event, componentTypeId: ModelID): void {
        this._setDefaultComponentTypeConfigMap(componentTypeId)
        this.config.component_type_config_map[componentTypeId].count_reset_frequency = $event;
    }

    beforeSave(): boolean {
        const map = this.config?.component_type_config_map || {};
        const offenders: string[] = [];
        for (const ct of this.config?.component_types ?? []) {
            const entry = map[ct.id];
            if (!entry?.formula?.formula) continue;        // no formula picked → nothing to check
            const reason = this.nameBuilder.validateFormulaForReset(
                entry.formula.formula, entry.count_reset_frequency, entry.automatic_count);
            if (reason) offenders.push(`${ct.attributes?.name ?? ct.id}: ${reason}`);
        }
        if (offenders.length === 0) return true;
        this.notification.openError(
            `Some component types have a name formula that can't be scoped correctly — ${offenders.join(' ')}`
        );
        return false;
    }
}
